import React, { useState, useEffect } from 'react';
import { Calculator, Shield, Target, Users, Plus, Radio, Package, Zap, CheckCircle, XCircle, X, Layers } from 'lucide-react';

const TerritoryCalculator = ({ onDataChange }) => {
  // Multi-layer defense system
  const [defenseLayers, setDefenseLayers] = useState([
    { 
      id: Date.now(), 
      name: 'Layer 1',  // Add custom name field
      type: 'CITY', 
      territoryInput: '',
      metric: 'km2',
      infrastructures: [{ id: 1, area: '', metric: 'km2' }],
      distanceFromPrevious: ''
    }
  ]);

  const addDefenseLayer = () => {
    const newLayerNumber = defenseLayers.length + 1;
    setDefenseLayers([...defenseLayers, {
      id: Date.now(),
      name: `Layer ${newLayerNumber}`,
      type: 'CITY',
      territoryInput: '',
      metric: 'km2',
      infrastructures: [{ id: 1, area: '', metric: 'km2' }],
      distanceFromPrevious: ''
    }]);
  };

  const removeDefenseLayer = (id) => {
    if (defenseLayers.length > 1) {
      setDefenseLayers(defenseLayers.filter(layer => layer.id !== id));
    }
  };

  const updateLayer = (id, field, value) => {
    setDefenseLayers(defenseLayers.map(layer => 
      layer.id === id ? { ...layer, [field]: value } : layer
    ));
  };

  const updateLayerInfrastructure = (layerId, infraId, field, value) => {
    setDefenseLayers(defenseLayers.map(layer => {
      if (layer.id === layerId) {
        return {
          ...layer,
          infrastructures: layer.infrastructures.map(infra =>
            infra.id === infraId ? { ...infra, [field]: value } : infra
          )
        };
      }
      return layer;
    }));
  };

  const addInfrastructureToLayer = (layerId) => {
    setDefenseLayers(defenseLayers.map(layer => {
      if (layer.id === layerId) {
        return {
          ...layer,
          infrastructures: [...layer.infrastructures, { id: Date.now(), area: '', metric: 'km2' }]
        };
      }
      return layer;
    }));
  };

  const removeInfrastructureFromLayer = (layerId, infraId) => {
    setDefenseLayers(defenseLayers.map(layer => {
      if (layer.id === layerId && layer.infrastructures.length > 1) {
        return {
          ...layer,
          infrastructures: layer.infrastructures.filter(infra => infra.id !== infraId)
        };
      }
      return layer;
    }));
  };
  
  const [recommended, setRecommended] = useState(null);
  
  // Custom drone config (optional)
  const [droneConfig, setDroneConfig] = useState({
    antiDrone: '',
    antiTank: '',
    antiPersonnel: ''
  });
  
  // Granular reserves
  const [reserves, setReserves] = useState({
    droneBodies: '',
    antiDroneHeads: '',
    antiTankHeads: '',
    antiPersonnelHeads: '',
    modulesOnly: ''
  });

  // Standalone reserves purchase mode
  const [standaloneMode, setStandaloneMode] = useState(false);

  // Environmental questions
  const [environment, setEnvironment] = useState({
    country: '',
    region: '',
    isCriticalZone: false,
    hasCentralPower: false,
    needsIndependentPower: false,
    needsCamouflage: false
  });

  const DRONES_PER_MODULE = 9;
  const MODULE_PRICE = 90000;
  const DRONE_BODY_PRICE = 8000;
  const HEAD_PRICE = 2000;
  const MODULE_ONLY_PRICE = 18000;
  const RADAR_PRICE = 20000;
  const DIGITAL_MAPPING_PRICE = 10000;
  const INSTALLATION_BASE = 10000;
  const CONTROL_HUB_PRICE = 30000;

  const formatPrice = (value) => {
    if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(0)}K`;
    } else {
      return `€${value.toFixed(0)}`;
    }
  };

  const addInfrastructure = () => {
    setInfrastructures([...infrastructures, { id: Date.now(), area: '', metric: 'km2' }]);
  };

  const removeInfrastructure = (id) => {
    if (infrastructures.length > 1) {
      setInfrastructures(infrastructures.filter(inf => inf.id !== id));
    }
  };

  const updateInfrastructure = (id, field, value) => {
    setInfrastructures(infrastructures.map(inf => 
      inf.id === id ? { ...inf, [field]: value } : inf
    ));
  };

  // Calculate combined recommendations from all layers
  useEffect(() => {
    let totalModules = 0;
    let totalDrones = 0;
    let totalRadars = 0;
    let totalKm2 = 0;
    let totalInstallationCost = 0;
    
    defenseLayers.forEach(layer => {
      if (layer.type !== 'INFRASTRUCTURE' && (!layer.territoryInput || parseFloat(layer.territoryInput) <= 0)) {
        return;
      }

      if (layer.type === 'INFRASTRUCTURE') {
        let layerM2 = 0;
        let layerRadars = 0;
        
        layer.infrastructures.forEach(inf => {
          const val = parseFloat(inf.area) || 0;
          const m2 = inf.metric === 'km2' ? val * 1000000 : val;
          layerM2 += m2;
          
          if (inf.metric === 'km2') {
            layerRadars += Math.ceil(val / 1.5);
          } else {
            layerRadars += Math.ceil(val / 1500);
          }
        });
        
        layerRadars = Math.max(1, layerRadars);
        
        if (layerM2 > 0) {
          const modules = Math.ceil(layerM2 / 100);
          totalModules += modules;
          totalDrones += modules * DRONES_PER_MODULE;
          totalRadars += layerRadars;
          totalKm2 += layerM2 / 1000000;
          totalInstallationCost += modules * (INSTALLATION_BASE / 10);
        }
      } else if (layer.type === 'CITY') {
        const areaVal = parseFloat(layer.territoryInput) || 0;
        const areaM2 = layer.metric === 'km2' ? areaVal * 1000000 : areaVal * 1000000;
        
        if (areaM2 > 0) {
          const modules = Math.ceil(areaM2 / 100000);
          totalModules += modules;
          totalDrones += modules * DRONES_PER_MODULE;
          totalRadars += Math.ceil(modules / 10);
          totalKm2 += areaM2 / 1000000;
          totalInstallationCost += modules * INSTALLATION_BASE;
        }
      } else if (layer.type === 'LINE') {
        const perimeterKm = parseFloat(layer.territoryInput) || 0;
        
        if (perimeterKm > 0) {
          const modules = Math.ceil(perimeterKm * 2);
          totalModules += modules;
          totalDrones += modules * DRONES_PER_MODULE;
          totalRadars += Math.ceil(modules / 8);
          totalKm2 += perimeterKm;
          totalInstallationCost += modules * INSTALLATION_BASE;
        }
      }
    });

    if (totalModules > 0) {
      const antiDroneDefault = Math.ceil(totalDrones * 0.4);
      const antiTankDefault = Math.ceil(totalDrones * 0.35);
      const antiPersonnelDefault = totalDrones - antiDroneDefault - antiTankDefault;

      setRecommended({
        modulesNeeded: totalModules,
        totalDrones: totalDrones,
        antiDroneDefault,
        antiTankDefault,
        antiPersonnelDefault,
        defaultDistribution: {
          antiDrone: antiDroneDefault,
          antiTank: antiTankDefault,
          antiPersonnel: antiPersonnelDefault
        },
        radarUnits: totalRadars,
        installationCost: totalInstallationCost,
        mappingCost: DIGITAL_MAPPING_PRICE,
        controlHubCost: CONTROL_HUB_PRICE,
        totalKm2: totalKm2,
        layerCount: defenseLayers.length
      });
    } else {
      setRecommended(null);
      setDroneConfig({ antiDrone: '', antiTank: '', antiPersonnel: '' });
    }
  }, [defenseLayers]);

  // Send calculator data to parent component whenever it changes
  useEffect(() => {
    if (onDataChange && recommended) {
      const calculatorData = {
        layers: defenseLayers.map(layer => ({
          name: layer.name,  // FIXED: Include custom layer name
          type: layer.type,
          territoryInput: layer.territoryInput,
          metric: layer.metric,
          infrastructures: layer.infrastructures,
          distanceFromPrevious: layer.distanceFromPrevious
        })),
        recommended: {
          modulesNeeded: recommended.modulesNeeded,
          totalDrones: recommended.totalDrones,
          radarUnits: recommended.radarUnits,
          totalKm2: recommended.totalKm2,
          layerCount: recommended.layerCount,
          installationCost: recommended.installationCost
        },
        droneConfig: {
          antiDrone: parseInt(droneConfig.antiDrone) || recommended.defaultDistribution.antiDrone,
          antiTank: parseInt(droneConfig.antiTank) || recommended.defaultDistribution.antiTank,
          antiPersonnel: parseInt(droneConfig.antiPersonnel) || recommended.defaultDistribution.antiPersonnel
        },
        reserves: {
          droneBodies: parseInt(reserves.droneBodies) || 0,
          antiDroneHeads: parseInt(reserves.antiDroneHeads) || 0,
          antiTankHeads: parseInt(reserves.antiTankHeads) || 0,
          antiPersonnelHeads: parseInt(reserves.antiPersonnelHeads) || 0,
          modulesOnly: parseInt(reserves.modulesOnly) || 0
        },
        environment,
        totalCost: calculateTotalCost(),
        deploymentType: getDeploymentType(),
        standaloneMode
      };
      onDataChange(calculatorData);
    }
  }, [defenseLayers, recommended, droneConfig, reserves, environment, standaloneMode, onDataChange]);


  const getRemainingDrones = () => {
    if (!recommended) return 0;
    return recommended.totalDrones - getTotalAssigned();
  };

  const getActualDistribution = () => {
    if (!recommended) return null;
    
    const anti = parseInt(droneConfig.antiDrone) || recommended.defaultDistribution.antiDrone;
    const tank = parseInt(droneConfig.antiTank) || recommended.defaultDistribution.antiTank;
    const personnel = parseInt(droneConfig.antiPersonnel) || recommended.defaultDistribution.antiPersonnel;
    
    return { antiDrone: anti, antiTank: tank, antiPersonnel: personnel };
  };

  const getTotalAssigned = () => {
    const anti = parseInt(droneConfig.antiDrone) || 0;
    const tank = parseInt(droneConfig.antiTank) || 0;
    const personnel = parseInt(droneConfig.antiPersonnel) || 0;
    return anti + tank + personnel;
  };

  const calculateTotalCost = () => {
    let totalCost = 0;

    // Reserves (always calculated)
    const droneBodies = parseInt(reserves.droneBodies) || 0;
    const antiDroneHeads = parseInt(reserves.antiDroneHeads) || 0;
    const antiTankHeads = parseInt(reserves.antiTankHeads) || 0;
    const antiPersonnelHeads = parseInt(reserves.antiPersonnelHeads) || 0;
    const modulesOnly = parseInt(reserves.modulesOnly) || 0;

    totalCost += droneBodies * DRONE_BODY_PRICE;
    totalCost += antiDroneHeads * HEAD_PRICE;
    totalCost += antiTankHeads * HEAD_PRICE;
    totalCost += antiPersonnelHeads * HEAD_PRICE;
    totalCost += modulesOnly * MODULE_ONLY_PRICE;

    // Base system costs (skip if standalone mode)
    if (!standaloneMode && recommended) {
      totalCost += recommended.modulesNeeded * MODULE_PRICE;
      totalCost += recommended.radarUnits * RADAR_PRICE;
      totalCost += recommended.installationCost;
      totalCost += DIGITAL_MAPPING_PRICE;
      totalCost += CONTROL_HUB_PRICE;
    }

    return totalCost;
  };

  const getDeploymentType = () => {
    if (!recommended) return 'Spare Parts Order';
    if (recommended.layerCount > 1) {
      return `Multi-Layer Defense (${recommended.layerCount} Layers)`;
    }
    if (recommended.modulesNeeded <= 10) return 'Small Deployment';
    if (recommended.modulesNeeded <= 50) return 'Regional Defense';
    return 'Large-Scale Defense';
  };

  const hasReserves = () => {
    return parseInt(reserves.droneBodies) > 0 ||
           parseInt(reserves.antiDroneHeads) > 0 ||
           parseInt(reserves.antiTankHeads) > 0 ||
           parseInt(reserves.antiPersonnelHeads) > 0 ||
           parseInt(reserves.modulesOnly) > 0;
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      background: '#0A0A0A',
      border: '1px solid rgba(212, 175, 55, 0.2)',
      padding: '60px'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <Calculator size={32} style={{ color: '#D4AF37', margin: '0 auto 16px', display: 'block' }} />
        <h3 style={{ fontSize: '32px', fontWeight: '700', color: '#FFFFFF', marginBottom: '12px' }}>
          Territory Protection Calculator
        </h3>
        <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)' }}>
          Professional defense deployment planning
        </p>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Multi-Layer Defense System */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#FFFFFF',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              <Layers size={18} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
              Defense Layers ({defenseLayers.length})
            </label>
            <button
              type="button"
              onClick={addDefenseLayer}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                border: 'none',
                color: '#000000',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              <Plus size={16} />
              Add Layer
            </button>
          </div>

          {/* Defense Layer Cards */}
          {!standaloneMode && defenseLayers.map((layer, index) => (
            <div key={layer.id} style={{
              marginBottom: '24px',
              padding: '24px',
              background: 'rgba(212, 175, 55, 0.05)',
              border: '2px solid rgba(212, 175, 55, 0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Layers size={18} style={{ color: '#D4AF37', flexShrink: 0 }} />
                  <input
                    type="text"
                    value={layer.name || `Layer ${index + 1}`}
                    onChange={(e) => {
                      setDefenseLayers(defenseLayers.map(l =>
                        l.id === layer.id ? { ...l, name: e.target.value } : l
                      ));
                    }}
                    placeholder={`e.g., Northern Border, Airport Zone`}
                    style={{
                      flex: 1,
                      maxWidth: '300px',
                      padding: '8px 12px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(212, 175, 55, 0.4)',
                      color: '#D4AF37',
                      fontSize: '14px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      outline: 'none'
                    }}
                  />
                  <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.4)', fontStyle: 'italic' }}>
                    #{index + 1}
                  </span>
                </div>
                {defenseLayers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDefenseLayer(layer.id)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(255, 0, 0, 0.1)',
                      border: '1px solid rgba(255, 0, 0, 0.3)',
                      color: '#FF6B6B',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <X size={14} />
                    Remove
                  </button>
                )}
              </div>

              {/* Distance from Previous Layer */}
              {index > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                    Distance from Layer {index} (km)
                  </label>
                  <input
                    type="number"
                    value={layer.distanceFromPrevious}
                    onChange={(e) => updateLayer(layer.id, 'distanceFromPrevious', e.target.value)}
                    placeholder="e.g., 5"
                    min="0"
                    step="0.1"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: '#000000',
                      border: '1px solid rgba(212, 175, 55, 0.2)',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              {/* Defense Type Selector for this Layer */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', display: 'block' }}>
                  Defense Type
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => updateLayer(layer.id, 'type', 'CITY')}
                    style={{
                      padding: '16px',
                      background: layer.type === 'CITY' ? 'rgba(212, 175, 55, 0.2)' : '#000000',
                      border: `2px solid ${layer.type === 'CITY' ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
                      color: layer.type === 'CITY' ? '#D4AF37' : '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textAlign: 'center',
                      textTransform: 'uppercase'
                    }}
                  >
                    CITY
                  </button>
                  <button
                    type="button"
                    onClick={() => updateLayer(layer.id, 'type', 'LINE')}
                    style={{
                      padding: '16px',
                      background: layer.type === 'LINE' ? 'rgba(212, 175, 55, 0.2)' : '#000000',
                      border: `2px solid ${layer.type === 'LINE' ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
                      color: layer.type === 'LINE' ? '#D4AF37' : '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textAlign: 'center',
                      textTransform: 'uppercase'
                    }}
                  >
                    LINE
                  </button>
                  <button
                    type="button"
                    onClick={() => updateLayer(layer.id, 'type', 'INFRASTRUCTURE')}
                    style={{
                      padding: '16px',
                      background: layer.type === 'INFRASTRUCTURE' ? 'rgba(212, 175, 55, 0.2)' : '#000000',
                      border: `2px solid ${layer.type === 'INFRASTRUCTURE' ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
                      color: layer.type === 'INFRASTRUCTURE' ? '#D4AF37' : '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textAlign: 'center',
                      textTransform: 'uppercase'
                    }}
                  >
                    INFRA
                  </button>
                </div>
              </div>

              {/* Territory Input based on type */}
              {layer.type !== 'INFRASTRUCTURE' && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                    {layer.type === 'CITY' ? 'City Area (km²)' : 'Border Perimeter (km)'}
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input
                      type="number"
                      value={layer.territoryInput}
                      onChange={(e) => updateLayer(layer.id, 'territoryInput', e.target.value)}
                      placeholder={layer.type === 'CITY' ? 'e.g., 50' : 'e.g., 100'}
                      min="0"
                      step="0.01"
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        background: '#000000',
                        border: '1px solid rgba(212, 175, 55, 0.2)',
                        color: '#FFFFFF',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                    {layer.type === 'CITY' && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => updateLayer(layer.id, 'metric', 'km2')}
                          style={{
                            padding: '12px 20px',
                            background: layer.metric === 'km2' ? '#D4AF37' : '#000000',
                            border: `1px solid ${layer.metric === 'km2' ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
                            color: layer.metric === 'km2' ? '#000000' : '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          km²
                        </button>
                        <button
                          type="button"
                          onClick={() => updateLayer(layer.id, 'metric', 'm2')}
                          style={{
                            padding: '12px 20px',
                            background: layer.metric === 'm2' ? '#D4AF37' : '#000000',
                            border: `1px solid ${layer.metric === 'm2' ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
                            color: layer.metric === 'm2' ? '#000000' : '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          m²
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Infrastructure Multiple Sites */}
              {layer.type === 'INFRASTRUCTURE' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Infrastructure Sites
                    </label>
                    <button
                      type="button"
                      onClick={() => addInfrastructureToLayer(layer.id)}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(212, 175, 55, 0.1)',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        color: '#D4AF37',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Plus size={12} />
                      Add Site
                    </button>
                  </div>

                  {layer.infrastructures.map((infra, infraIndex) => (
                    <div key={infra.id} style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <input
                          type="number"
                          value={infra.area}
                          onChange={(e) => updateLayerInfrastructure(layer.id, infra.id, 'area', e.target.value)}
                          placeholder="e.g., 0.005"
                          min="0"
                          step="0.001"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: '#000000',
                            border: '1px solid rgba(212, 175, 55, 0.2)',
                            color: '#FFFFFF',
                            fontSize: '16px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => updateLayerInfrastructure(layer.id, infra.id, 'metric', 'km2')}
                          style={{
                            padding: '12px 16px',
                            background: infra.metric === 'km2' ? '#D4AF37' : '#000000',
                            border: `1px solid ${infra.metric === 'km2' ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
                            color: infra.metric === 'km2' ? '#000000' : '#FFFFFF',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          km²
                        </button>
                        <button
                          type="button"
                          onClick={() => updateLayerInfrastructure(layer.id, infra.id, 'metric', 'm2')}
                          style={{
                            padding: '12px 16px',
                            background: infra.metric === 'm2' ? '#D4AF37' : '#000000',
                            border: `1px solid ${infra.metric === 'm2' ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
                            color: infra.metric === 'm2' ? '#000000' : '#FFFFFF',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          m²
                        </button>
                      </div>
                      {layer.infrastructures.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInfrastructureFromLayer(layer.id, infra.id)}
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 0, 0, 0.3)',
                            color: '#FF6B6B',
                            cursor: 'pointer'
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Environmental Questions */}
        <div style={{ marginBottom: '40px', background: 'rgba(0, 0, 0, 0.5)', padding: '24px', border: '1px solid rgba(212, 175, 55, 0.2)' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#D4AF37', marginBottom: '20px', textTransform: 'uppercase' }}>
            Operational Assessment
          </h4>
          
          {/* Location Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                Country *
              </label>
              <input
                type="text"
                value={environment.country}
                onChange={(e) => setEnvironment({...environment, country: e.target.value})}
                placeholder="e.g., Estonia"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#000000',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'block' }}>
                Region *
              </label>
              <input
                type="text"
                value={environment.region}
                onChange={(e) => setEnvironment({...environment, region: e.target.value})}
                placeholder="e.g., Tallinn, Harju County"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#000000',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Question 1 */}
            <div 
              onClick={() => setEnvironment({...environment, isCriticalZone: !environment.isCriticalZone})}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: '#000000', border: '1px solid rgba(212, 175, 55, 0.1)' }}
            >
              {environment.isCriticalZone ? 
                <CheckCircle size={20} style={{ color: '#D4AF37', flexShrink: 0 }} /> : 
                <XCircle size={20} style={{ color: '#666666', flexShrink: 0 }} />
              }
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>
                Is this territory currently in an active conflict zone or high-risk area?
              </span>
            </div>

            {/* Question 2 - Power Grid Assessment */}
            <div 
              onClick={() => setEnvironment({...environment, hasCentralPower: !environment.hasCentralPower, needsIndependentPower: false})}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: '#000000', border: '1px solid rgba(212, 175, 55, 0.1)' }}
            >
              {environment.hasCentralPower ? 
                <CheckCircle size={20} style={{ color: '#D4AF37', flexShrink: 0 }} /> : 
                <XCircle size={20} style={{ color: '#666666', flexShrink: 0 }} />
              }
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>
                Is reliable centralized power grid available for module operation?
              </span>
            </div>

            {/* Question 2.2 (conditional) - Shows when power grid IS available */}
            {environment.hasCentralPower && (
              <div 
                onClick={() => setEnvironment({...environment, needsIndependentPower: !environment.needsIndependentPower})}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', marginLeft: '32px', background: 'rgba(212, 175, 55, 0.05)', border: '1px solid rgba(212, 175, 55, 0.2)' }}
              >
                {environment.needsIndependentPower ? 
                  <CheckCircle size={20} style={{ color: '#D4AF37', flexShrink: 0 }} /> : 
                  <XCircle size={20} style={{ color: '#666666', flexShrink: 0 }} />
                }
                <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>
                  Independent power system (solar/generator) required for installation?
                </span>
              </div>
            )}

            {/* Question 3 */}
            <div 
              onClick={() => setEnvironment({...environment, needsCamouflage: !environment.needsCamouflage})}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: '#000000', border: '1px solid rgba(212, 175, 55, 0.1)' }}
            >
              {environment.needsCamouflage ? 
                <CheckCircle size={20} style={{ color: '#D4AF37', flexShrink: 0 }} /> : 
                <XCircle size={20} style={{ color: '#666666', flexShrink: 0 }} />
              }
              <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>
                Would tactical camouflage or stealth coating be beneficial for module concealment?
              </span>
            </div>
          </div>
        </div>

        {/* STANDALONE PURCHASE MODE - ALWAYS VISIBLE */}
        <div 
          onClick={() => setStandaloneMode(!standaloneMode)}
          style={{ 
            marginBottom: '40px', 
            padding: '24px', 
            background: standaloneMode ? 'rgba(212, 175, 55, 0.15)' : 'rgba(0, 0, 0, 0.5)', 
            border: `2px solid ${standaloneMode ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)'}`,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {standaloneMode ? 
              <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} /> : 
              <XCircle size={24} style={{ color: '#666666', flexShrink: 0 }} />
            }
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF', marginBottom: '8px' }}>
                Purchase Spare Parts Only (Without Base Deployment System)
              </div>
              <div style={{ fontSize: '13px', color: standaloneMode ? '#D4AF37' : '#666666' }}>
                {standaloneMode ? '✓ Standalone mode active - Base system configuration hidden' : 'Click to purchase reserves independently without full system deployment'}
              </div>
            </div>
          </div>
        </div>

        {/* BASE SYSTEM CONFIGURATION - HIDE IN STANDALONE MODE */}
        {!standaloneMode && (
          <>
        {/* Recommended Configuration */}
        {recommended && (
          <>
            <div style={{
              background: 'rgba(212, 175, 55, 0.05)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              padding: '32px',
              marginBottom: '40px'
            }}>
              <h4 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#D4AF37',
                marginBottom: '24px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Recommended Base Configuration
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666666', marginBottom: '8px', textTransform: 'uppercase' }}>Modules</div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#D4AF37' }}>{recommended.modulesNeeded}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666666', marginBottom: '8px', textTransform: 'uppercase' }}>Drones</div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#D4AF37' }}>{recommended.totalDrones}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#666666', marginBottom: '8px', textTransform: 'uppercase' }}>Radar Units</div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#D4AF37' }}>{recommended.radarUnits}</div>
                </div>
              </div>

              <div style={{ fontSize: '13px', color: '#666666', lineHeight: '1.6' }}>
                <p style={{ margin: '4px 0' }}>• {DRONES_PER_MODULE} drones per module</p>
                <p style={{ margin: '4px 0' }}>• Default distribution: {recommended.defaultDistribution.antiDrone} Anti-Drone, {recommended.defaultDistribution.antiTank} Anti-Tank, {recommended.defaultDistribution.antiPersonnel} Anti-Personnel</p>
                <p style={{ margin: '4px 0' }}>• {recommended.radarUnits} radar system{recommended.radarUnits > 1 ? 's' : ''} required</p>
              </div>
            </div>

            {/* Custom Drone Configuration */}
            <div style={{ marginBottom: '40px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#FFFFFF', marginBottom: '8px' }}>
                Custom Drone Configuration (Optional)
              </h4>
              <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                Customize drone distribution. Total cannot exceed {recommended.totalDrones} drones.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '16px' }}>
                <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <Shield size={24} style={{ color: '#00A8E8' }} />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Anti-Drone</span>
                  </div>
                  <input
                    type="number"
                    value={droneConfig.antiDrone}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      if (val <= recommended.totalDrones) {
                        setDroneConfig({...droneConfig, antiDrone: e.target.value});
                      }
                    }}
                    placeholder={recommended.defaultDistribution.antiDrone.toString()}
                    min="0"
                    max={recommended.totalDrones}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: '1px solid rgba(212, 175, 55, 0.2)',
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: '700',
                      textAlign: 'center',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <Target size={24} style={{ color: '#FF6B35' }} />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Anti-Tank</span>
                  </div>
                  <input
                    type="number"
                    value={droneConfig.antiTank}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      if (val <= recommended.totalDrones) {
                        setDroneConfig({...droneConfig, antiTank: e.target.value});
                      }
                    }}
                    placeholder={recommended.defaultDistribution.antiTank.toString()}
                    min="0"
                    max={recommended.totalDrones}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: '1px solid rgba(212, 175, 55, 0.2)',
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: '700',
                      textAlign: 'center',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <Users size={24} style={{ color: '#D4AF37' }} />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Anti-Personnel</span>
                  </div>
                  <input
                    type="number"
                    value={droneConfig.antiPersonnel}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      if (val <= recommended.totalDrones) {
                        setDroneConfig({...droneConfig, antiPersonnel: e.target.value});
                      }
                    }}
                    placeholder={recommended.defaultDistribution.antiPersonnel.toString()}
                    min="0"
                    max={recommended.totalDrones}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: '1px solid rgba(212, 175, 55, 0.2)',
                      color: '#FFFFFF',
                      fontSize: '18px',
                      fontWeight: '700',
                      textAlign: 'center',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{
                padding: '12px 20px',
                background: getTotalAssigned() > recommended.totalDrones ? 'rgba(255, 107, 53, 0.1)' : 'rgba(212, 175, 55, 0.1)',
                border: `1px solid ${getTotalAssigned() > recommended.totalDrones ? '#FF6B35' : 'rgba(212, 175, 55, 0.3)'}`,
                fontSize: '14px',
                color: getTotalAssigned() > recommended.totalDrones ? '#FF6B35' : '#D4AF37'
              }}>
                {getTotalAssigned() > 0 ? (
                  <>
                    Assigned: {getTotalAssigned()} / {recommended.totalDrones} drones
                    {getTotalAssigned() > recommended.totalDrones && ' (Exceeds limit!)'}
                    {getTotalAssigned() <= recommended.totalDrones && getRemainingDrones() > 0 && ` • ${getRemainingDrones()} remaining`}
                  </>
                ) : (
                  `Using default distribution (${recommended.totalDrones} drones)`
                )}
              </div>
            </div>
          </>
        )}
          </>
        )}

        {/* RESERVES SECTION - ALWAYS VISIBLE */}
        <div style={{ marginBottom: '40px' }}>
          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#FFFFFF', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={20} style={{ color: '#D4AF37' }} />
            Additional Reserves & Spare Parts
          </h4>
          <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
            Order spare parts for maintenance and replacements. No quantity limits.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {/* Drone Bodies */}
            <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Package size={20} style={{ color: '#D4AF37' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Drone Bodies</div>
                  <div style={{ fontSize: '11px', color: '#666666' }}>{formatPrice(DRONE_BODY_PRICE)} each</div>
                </div>
              </div>
              <input
                type="number"
                value={reserves.droneBodies}
                onChange={(e) => setReserves({...reserves, droneBodies: e.target.value})}
                placeholder="0"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Shield size={20} style={{ color: '#00A8E8' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Anti-Drone Heads</div>
                  <div style={{ fontSize: '11px', color: '#666666' }}>{formatPrice(HEAD_PRICE)} each</div>
                </div>
              </div>
              <input
                type="number"
                value={reserves.antiDroneHeads}
                onChange={(e) => setReserves({...reserves, antiDroneHeads: e.target.value})}
                placeholder="0"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Target size={20} style={{ color: '#FF6B35' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Anti-Tank Heads</div>
                  <div style={{ fontSize: '11px', color: '#666666' }}>{formatPrice(HEAD_PRICE)} each</div>
                </div>
              </div>
              <input
                type="number"
                value={reserves.antiTankHeads}
                onChange={(e) => setReserves({...reserves, antiTankHeads: e.target.value})}
                placeholder="0"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Users size={20} style={{ color: '#D4AF37' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Anti-Personnel Heads</div>
                  <div style={{ fontSize: '11px', color: '#666666' }}>{formatPrice(HEAD_PRICE)} each</div>
                </div>
              </div>
              <input
                type="number"
                value={reserves.antiPersonnelHeads}
                onChange={(e) => setReserves({...reserves, antiPersonnelHeads: e.target.value})}
                placeholder="0"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ background: '#000000', border: '1px solid rgba(212, 175, 55, 0.2)', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Radio size={20} style={{ color: '#D4AF37' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase' }}>Modules Only</div>
                  <div style={{ fontSize: '11px', color: '#666666' }}>{formatPrice(MODULE_ONLY_PRICE)} each</div>
                </div>
              </div>
              <input
                type="number"
                value={reserves.modulesOnly}
                onChange={(e) => setReserves({...reserves, modulesOnly: e.target.value})}
                placeholder="0"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Final Summary - Show when there's recommended config OR reserves */}
      {((recommended && getTotalAssigned() <= recommended.totalDrones) || hasReserves()) && (
        <div style={{ marginTop: '60px', paddingTop: '60px', borderTop: '1px solid rgba(212, 175, 55, 0.2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', flexWrap: 'wrap', gap: '20px' }}>
            <h4 style={{ fontSize: '24px', fontWeight: '700', color: '#FFFFFF' }}>
              Deployment Summary
            </h4>
            <span style={{
              padding: '8px 16px',
              background: 'rgba(212, 175, 55, 0.1)',
              border: '1px solid #D4AF37',
              color: '#D4AF37',
              fontSize: '12px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {getDeploymentType()}
            </span>
          </div>

          {/* Base Deployment (hide in standalone mode OR when no recommended config) */}
          {!standaloneMode && recommended && (
            <div style={{ background: 'rgba(0, 0, 0, 0.5)', padding: '32px', marginBottom: '24px' }}>
              <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#D4AF37', marginBottom: '20px', textTransform: 'uppercase' }}>Base Deployment</h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{recommended.modulesNeeded} Modules × {formatPrice(MODULE_PRICE)}</span>
                <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(recommended.modulesNeeded * MODULE_PRICE)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{recommended.radarUnits} Radar System{recommended.radarUnits > 1 ? 's' : ''} × {formatPrice(RADAR_PRICE)}</span>
                <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(recommended.radarUnits * RADAR_PRICE)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>Installation Cost (Base {formatPrice(INSTALLATION_BASE)} + Variable)</span>
                <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(recommended.installationCost)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>Digital Surround Mapping</span>
                <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(DIGITAL_MAPPING_PRICE)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>Control & Analysis Hub Infrastructure</span>
                <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(CONTROL_HUB_PRICE)}</span>
              </div>
            </div>
          </div>
          )}

          {/* Reserves Breakdown */}
          {(parseInt(reserves.droneBodies) > 0 || parseInt(reserves.antiDroneHeads) > 0 || parseInt(reserves.antiTankHeads) > 0 || parseInt(reserves.antiPersonnelHeads) > 0 || parseInt(reserves.modulesOnly) > 0) && (
            <div style={{ background: 'rgba(0, 0, 0, 0.5)', padding: '32px', marginBottom: '24px' }}>
              <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#D4AF37', marginBottom: '20px', textTransform: 'uppercase' }}>Reserves & Spare Parts</h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {parseInt(reserves.droneBodies) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{reserves.droneBodies} Drone Bodies × {formatPrice(DRONE_BODY_PRICE)}</span>
                    <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(parseInt(reserves.droneBodies) * DRONE_BODY_PRICE)}</span>
                  </div>
                )}
                {parseInt(reserves.antiDroneHeads) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{reserves.antiDroneHeads} Anti-Drone Heads × {formatPrice(HEAD_PRICE)}</span>
                    <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(parseInt(reserves.antiDroneHeads) * HEAD_PRICE)}</span>
                  </div>
                )}
                {parseInt(reserves.antiTankHeads) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{reserves.antiTankHeads} Anti-Tank Heads × {formatPrice(HEAD_PRICE)}</span>
                    <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(parseInt(reserves.antiTankHeads) * HEAD_PRICE)}</span>
                  </div>
                )}
                {parseInt(reserves.antiPersonnelHeads) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{reserves.antiPersonnelHeads} Anti-Personnel Heads × {formatPrice(HEAD_PRICE)}</span>
                    <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(parseInt(reserves.antiPersonnelHeads) * HEAD_PRICE)}</span>
                  </div>
                )}
                {parseInt(reserves.modulesOnly) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(212, 175, 55, 0.1)' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{reserves.modulesOnly} Modules Only × {formatPrice(MODULE_ONLY_PRICE)}</span>
                    <span style={{ fontWeight: '700', color: '#D4AF37' }}>{formatPrice(parseInt(reserves.modulesOnly) * MODULE_ONLY_PRICE)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Drone Configuration (hide in standalone mode OR when no recommended config) */}
          {!standaloneMode && recommended && (
            <div style={{ background: 'rgba(0, 0, 0, 0.5)', padding: '32px', marginBottom: '32px' }}>
              <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#D4AF37', marginBottom: '20px', textTransform: 'uppercase' }}>Final Drone Configuration</h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {(() => {
                const distribution = getActualDistribution();
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px', background: '#000000' }}>
                      <Shield size={20} style={{ color: '#00A8E8' }} />
                      <span style={{ flex: 1, fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>Anti-Drone Units</span>
                      <span style={{ fontSize: '20px', fontWeight: '700', color: '#D4AF37' }}>{distribution.antiDrone}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px', background: '#000000' }}>
                      <Target size={20} style={{ color: '#FF6B35' }} />
                      <span style={{ flex: 1, fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>Anti-Tank Units</span>
                      <span style={{ fontSize: '20px', fontWeight: '700', color: '#D4AF37' }}>{distribution.antiTank}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px', background: '#000000' }}>
                      <Users size={20} style={{ color: '#D4AF37' }} />
                      <span style={{ flex: 1, fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>Anti-Personnel Units</span>
                      <span style={{ fontSize: '20px', fontWeight: '700', color: '#D4AF37' }}>{distribution.antiPersonnel}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          )}

          {/* Total Cost */}
          <div style={{ padding: '32px', background: 'rgba(212, 175, 55, 0.1)', border: '2px solid #D4AF37' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '20px', fontWeight: '700', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Estimated Cost</span>
              <span style={{ fontSize: '36px', fontWeight: '700', color: '#D4AF37' }}>{formatPrice(calculateTotalCost())}</span>
            </div>
          </div>

          {/* Notes */}
          <div style={{ fontSize: '12px', color: '#666666', lineHeight: '1.8', marginTop: '24px' }}>
            <p style={{ margin: '4px 0' }}>* Prices are estimates and subject to final contract negotiations</p>
            <p style={{ margin: '4px 0' }}>* Installation includes {formatPrice(INSTALLATION_BASE)} base fee + variable costs</p>
            <p style={{ margin: '4px 0' }}>* Radar systems: 1 per 30 km² (CITY), 1 per 20 km (LINE), 1 per site + 1 per 1.5 km² if total exceeds 1.5 km² (INFRASTRUCTURE)</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerritoryCalculator;
